import {FC, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, Delete, Save} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {ApiError} from '../../models/apiError';
import {
    COSTUME_CATEGORY_ICON_OPTIONS,
    COSTUME_RENDER_KINDS,
    CostumeCategory,
    CostumeRenderKind,
    PatchCostumeCategoryRequest,
    RENDER_KIND_CHIP_COLOR,
    RENDER_KIND_LABEL_KO,
} from '../../types/costumeCategory';
import {
    useCostumeCategories,
    useDeleteCostumeCategory,
    useNormalizeCostumeCategories,
    usePatchCostumeCategory,
} from '../../hooks/useCostumeCategory';
import {EppiCategoryAddDialog} from './EppiCategoryAddDialog';

const TRANSLATION_LANGS: Array<{key: 'ko' | 'en' | 'ja' | 'vi'; label: string}> = [
    {key: 'ko', label: '한국어'},
    {key: 'en', label: 'English'},
    {key: 'ja', label: '日本語'},
    {key: 'vi', label: 'Tiếng Việt'},
];

interface RowState {
    renderKind: CostumeRenderKind;
    sortOrder: number;
    iconCode: string;
    isActive: boolean;
    displayNameKo: string;
    displayNameEn: string;
    displayNameJa: string;
    displayNameVi: string;
}

const buildRowState = (c: CostumeCategory): RowState => ({
    renderKind: c.renderKind,
    sortOrder: c.sortOrder,
    iconCode: c.iconCode ?? '',
    isActive: c.isActive,
    displayNameKo: c.displayNameKo,
    displayNameEn: c.displayNameEn,
    displayNameJa: c.displayNameJa,
    displayNameVi: c.displayNameVi,
});

interface DeleteBlockedState {
    open: boolean;
    categoryId: number | null;
    usedByCount: number;
    message: string;
}

export const EppiCategoryList: FC = () => {
    const navigate = useNavigate();
    const {data, isLoading, error} = useCostumeCategories();
    const patchMut = usePatchCostumeCategory();
    const deleteMut = useDeleteCostumeCategory();
    const normalizeMut = useNormalizeCostumeCategories();

    const [rowState, setRowState] = useState<Record<number, RowState>>({});
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [deleteBlocked, setDeleteBlocked] = useState<DeleteBlockedState>({
        open: false,
        categoryId: null,
        usedByCount: 0,
        message: '',
    });

    useEffect(() => {
        if (!data) return;
        const next: Record<number, RowState> = {};
        for (const c of data) {
            next[c.id] = buildRowState(c);
        }
        setRowState(next);
    }, [data]);

    const sortedCategories = useMemo(() => {
        if (!data) return [];
        return [...data].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
        );
    }, [data]);

    const nextSortOrder = useMemo(() => {
        if (!data || data.length === 0) return 1;
        return Math.max(...data.map((c) => c.sortOrder)) + 1;
    }, [data]);

    const handleChange = <K extends keyof RowState>(
        id: number,
        key: K,
        value: RowState[K]
    ) => {
        setRowState((prev) => ({
            ...prev,
            [id]: {...prev[id], [key]: value},
        }));
    };

    const handleSaveRow = async (c: CostumeCategory) => {
        const state = rowState[c.id];
        if (!state) return;
        const trimmedIcon = state.iconCode.trim();
        const req: PatchCostumeCategoryRequest = {
            renderKind: state.renderKind !== c.renderKind ? state.renderKind : undefined,
            sortOrder: state.sortOrder !== c.sortOrder ? state.sortOrder : undefined,
            isActive: state.isActive !== c.isActive ? state.isActive : undefined,
            displayNameKo:
                state.displayNameKo !== c.displayNameKo ? state.displayNameKo : undefined,
            displayNameEn:
                state.displayNameEn !== c.displayNameEn ? state.displayNameEn : undefined,
            displayNameJa:
                state.displayNameJa !== c.displayNameJa ? state.displayNameJa : undefined,
            displayNameVi:
                state.displayNameVi !== c.displayNameVi ? state.displayNameVi : undefined,
            iconCode:
                trimmedIcon !== (c.iconCode ?? '')
                    ? trimmedIcon.length > 0
                        ? trimmedIcon
                        : ''
                    : undefined,
        };
        try {
            await patchMut.mutateAsync({id: c.id, data: req});
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : '저장 실패';
            alert(`저장 실패: ${message}`);
        }
    };

    const handleDelete = async (c: CostumeCategory) => {
        if (c.isSystem) return;
        if (!confirm(`카테고리 "${c.code}" 를 삭제하시겠습니까?`)) return;
        try {
            await deleteMut.mutateAsync(c.id);
        } catch (e) {
            if (e instanceof ApiError && (e.status === 409 || e.errorCode === 5043)) {
                // 백엔드 메시지에서 usedByCount 추출 시도 (예: "사용 중 (12개)")
                const match = (e.message ?? '').match(/(\d+)/);
                const usedByCount = match ? Number(match[1]) || 0 : 0;
                setDeleteBlocked({
                    open: true,
                    categoryId: c.id,
                    usedByCount,
                    message: e.message ?? '',
                });
            } else if (e instanceof ApiError && (e.status === 403 || e.errorCode === 5042)) {
                alert('시스템 카테고리는 삭제할 수 없습니다.');
            } else {
                const message = e instanceof Error ? e.message : '삭제 실패';
                alert(`삭제 실패: ${message}`);
            }
        }
    };

    const handleNormalize = async () => {
        if (!confirm('현재 정렬 순서대로 sortOrder 를 1부터 다시 매깁니다. 진행하시겠습니까?'))
            return;
        try {
            await normalizeMut.mutateAsync();
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : '재정렬 실패';
            alert(`재정렬 실패: ${message}`);
        }
    };

    const goToBlockedCostumes = () => {
        if (!deleteBlocked.categoryId) return;
        navigate(`/eppi-costumes?categoryId=${deleteBlocked.categoryId}`);
        setDeleteBlocked({open: false, categoryId: null, usedByCount: 0, message: ''});
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">카테고리 목록을 불러올 수 없습니다.</Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Typography variant="h4">에삐 카테고리 관리</Typography>
                <Stack direction="row" gap={1}>
                    <Button
                        variant="outlined"
                        onClick={handleNormalize}
                        disabled={normalizeMut.isPending}
                    >
                        sort_order 1부터 재정렬
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setAddDialogOpen(true)}
                    >
                        카테고리 추가
                    </Button>
                </Stack>
            </Box>

            <Alert severity="info" sx={{mb: 2}}>
                카테고리는 모바일 스티커북 chip 과 어드민 코스튬 분류로 함께 쓰입니다.
                시스템 카테고리(`is_system=1`) 는 삭제할 수 없고, 사용 중인 카테고리는
                코스튬 이동 후에만 삭제 가능합니다. sortOrder 변경 시 사이 범위가 자동 밀립니다.
            </Alert>

            <Card>
                <CardContent sx={{p: 0}}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={48}>#</TableCell>
                                    <TableCell>code</TableCell>
                                    <TableCell width={180}>renderKind</TableCell>
                                    {TRANSLATION_LANGS.map((l) => (
                                        <TableCell key={l.key} width={140}>
                                            {l.label}
                                        </TableCell>
                                    ))}
                                    <TableCell width={140}>icon</TableCell>
                                    <TableCell width={100}>sortOrder</TableCell>
                                    <TableCell width={80}>활성</TableCell>
                                    <TableCell width={160} align="right">
                                        작업
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedCategories.map((c) => {
                                    const state = rowState[c.id];
                                    if (!state) return null;
                                    return (
                                        <TableRow key={c.id} hover>
                                            <TableCell>{c.id}</TableCell>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    <code>{c.code}</code>
                                                    {c.isSystem && (
                                                        <Chip
                                                            label="시스템"
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                        />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={state.renderKind}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            c.id,
                                                            'renderKind',
                                                            e.target
                                                                .value as CostumeRenderKind
                                                        )
                                                    }
                                                    fullWidth
                                                >
                                                    {COSTUME_RENDER_KINDS.map((k) => (
                                                        <MenuItem key={k} value={k}>
                                                            <Chip
                                                                label={
                                                                    RENDER_KIND_LABEL_KO[k]
                                                                }
                                                                size="small"
                                                                color={
                                                                    RENDER_KIND_CHIP_COLOR[k]
                                                                }
                                                                sx={{mr: 1}}
                                                            />
                                                            {k}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </TableCell>
                                            {TRANSLATION_LANGS.map((l) => {
                                                const stateKey = (
                                                    'displayName' +
                                                    l.key.charAt(0).toUpperCase() +
                                                    l.key.slice(1)
                                                ) as keyof RowState;
                                                return (
                                                    <TableCell key={l.key}>
                                                        <TextField
                                                            size="small"
                                                            value={
                                                                (state[
                                                                    stateKey
                                                                ] as string) ?? ''
                                                            }
                                                            onChange={(e) =>
                                                                handleChange(
                                                                    c.id,
                                                                    stateKey,
                                                                    e.target.value as never
                                                                )
                                                            }
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell>
                                                <TextField
                                                    select
                                                    size="small"
                                                    value={state.iconCode}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            c.id,
                                                            'iconCode',
                                                            e.target.value
                                                        )
                                                    }
                                                    fullWidth
                                                >
                                                    <MenuItem value="">
                                                        <em>(없음)</em>
                                                    </MenuItem>
                                                    {COSTUME_CATEGORY_ICON_OPTIONS.map(
                                                        (opt) => (
                                                            <MenuItem
                                                                key={opt.code}
                                                                value={opt.code}
                                                            >
                                                                {opt.label}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={state.sortOrder}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            c.id,
                                                            'sortOrder',
                                                            Number(e.target.value)
                                                        )
                                                    }
                                                    sx={{width: 80}}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Switch
                                                    checked={state.isActive}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            c.id,
                                                            'isActive',
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack
                                                    direction="row"
                                                    gap={0.5}
                                                    justifyContent="flex-end"
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        startIcon={<Save />}
                                                        onClick={() => handleSaveRow(c)}
                                                        disabled={patchMut.isPending}
                                                    >
                                                        저장
                                                    </Button>
                                                    <Tooltip
                                                        title={
                                                            c.isSystem
                                                                ? '시스템 카테고리는 삭제할 수 없습니다'
                                                                : ''
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() =>
                                                                    handleDelete(c)
                                                                }
                                                                disabled={
                                                                    c.isSystem ||
                                                                    deleteMut.isPending
                                                                }
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {sortedCategories.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <Typography color="text.secondary" py={4}>
                                                카테고리가 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <EppiCategoryAddDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                nextSortOrder={nextSortOrder}
            />

            <Dialog
                open={deleteBlocked.open}
                onClose={() =>
                    setDeleteBlocked({
                        open: false,
                        categoryId: null,
                        usedByCount: 0,
                        message: '',
                    })
                }
            >
                <DialogTitle>카테고리 삭제 차단</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        이 카테고리를 사용하는 코스튬이{' '}
                        {deleteBlocked.usedByCount > 0
                            ? `${deleteBlocked.usedByCount}개 `
                            : ''}
                        있어 삭제할 수 없습니다. 먼저 해당 코스튬을 다른 카테고리로
                        이동시킨 뒤 다시 시도하세요.
                    </DialogContentText>
                    {deleteBlocked.message && (
                        <Alert severity="warning" sx={{mt: 2}}>
                            {deleteBlocked.message}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setDeleteBlocked({
                                open: false,
                                categoryId: null,
                                usedByCount: 0,
                                message: '',
                            })
                        }
                    >
                        닫기
                    </Button>
                    <Button variant="contained" onClick={goToBlockedCostumes}>
                        코스튬 목록 보기 →
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
